import { apiClient, getCurrentUserEmail } from "../apiClient";
import { TUser } from "../../common/types/user";
import { TResponseItemWrapper, TResponseListWrapper } from "../../common/types/common";

export const getUserList = async ({ pagination: { pageSize, pageIndex }, sorting: { sortBy, isDescending}, filtering: { searchTerm } }): Promise<TResponseListWrapper<TUser>> => {
    let sorting = sortBy ? `&sortBy=${encodeURIComponent(sortBy)}`: '';
    if (sortBy && isDescending) {
        sorting += `&isDescending=${encodeURIComponent(isDescending)}`;
    }

    const search = searchTerm ? `&searchTerm=${encodeURIComponent(searchTerm)}` : '';

    return apiClient.get(`/User/GetUsers?pageSize=${encodeURIComponent(pageSize)}&pageNumber=${encodeURIComponent(pageIndex)}${sorting}${search}`);
};

export const getUser = async (userId: string | number): Promise<TResponseItemWrapper<TUser>> => {
    return apiClient.get(`/User/GetUser?userId=${encodeURIComponent(userId)}`);
};

export const getAuthenticatedUser = async (): Promise<TResponseItemWrapper<TUser>> => {
    // FEATURE FLAG: AuthRequired bypass — include userEmail param so the API can resolve the user
    // when auth enforcement is off and no JWT email claim is present.
    const email = getCurrentUserEmail();
    const query = email ? `?userEmail=${encodeURIComponent(email)}` : '';
    return apiClient.get(`/User/GetAuthenticatedUser${query}`);
};

export const getUserByEmail = async (email): Promise<TResponseItemWrapper<TUser>> => {
    return apiClient.get(`/User/GetUserByEmail?email=${encodeURIComponent(email)}`);
};

export const createUser = async (user): Promise<TResponseItemWrapper<TUser>> => {
    // FEATURE FLAG: AuthRequired bypass — include userEmail param so the API can resolve the caller
    // when auth enforcement is off and no JWT email claim is present.
    const email = getCurrentUserEmail();
    const query = email ? `?userEmail=${encodeURIComponent(email)}` : '';
    return apiClient.post(`/User/CreateUser${query}`, user);
};

export const updateUser = async (user: TUser): Promise<TResponseItemWrapper<TUser>> => {
    return apiClient.post('/User/UpdateUser', user);
};

const withCallerEmail = (path: string, callerEmail?: string) => {
    if (!callerEmail) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}userEmail=${encodeURIComponent(callerEmail)}`;
};

export const grantAdmin = async (userId: number, callerEmail?: string) => {
    return apiClient.post(withCallerEmail(`/UserAdmin/GrantAdmin?userId=${encodeURIComponent(userId)}`, callerEmail));
};

export const revokeAdmin = async (userId: number, callerEmail?: string) => {
    return apiClient.post(withCallerEmail(`/UserAdmin/RevokeAdmin?userId=${encodeURIComponent(userId)}`, callerEmail));
};

export const addUserAppRole = async (
    userEmail: string,
    roleName: string,
    callerEmail?: string,
): Promise<TResponseItemWrapper<string>> => {
    return apiClient.post(
        withCallerEmail(`/UserAdmin/AddRole/${encodeURIComponent(userEmail)}`, callerEmail),
        { roleName },
    );
};

export const removeUserAppRole = async (
    userEmail: string,
    roleName: string,
    callerEmail?: string,
): Promise<TResponseItemWrapper<string>> => {
    return apiClient.post(
        withCallerEmail(`/UserAdmin/RemoveRole/${encodeURIComponent(userEmail)}`, callerEmail),
        { roleName },
    );
};
